import React, { useState, useEffect, useRef } from "react";
import { UserCircle, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { NavDropPanel } from "./NavDropPanel";
import MyAccount from "../pages/MyAccount";
import { AccountSwitcher } from "./AccountSwitcher";
import { usePanelView } from "../lib/panelView";

// Any trigger (desktop tile, mobile nav, etc.) opens the account panel by
// firing this event — pressing the same button again closes it.
export const closeAccountModal = () => window.dispatchEvent(new CustomEvent("close-account-modal"));
export const openAccountModal = (tab) => window.dispatchEvent(new CustomEvent("open-account-modal", { detail: typeof tab === "string" ? tab : null }));

const ACCOUNT_TABS = (isOwner, admin) => [
    ["account", "Account"], ["home", "Favourites"], ["notifications", "Notifications"], ["objectives", "Objectives"],
    ...(isOwner ? [["finances", "Finances"], ["subscription", "Subscription"], ["reports", "Full Reports"], ["settings", "Settings"], ["storage", "Storage Centre"], ["leagues", "All Leagues"]] : []),
    ...(isOwner || admin ? [["accounts", "Accounts & Users"]] : []),
];
const WIDE = new Set(["finances", "reports", "accounts", "subscription", "settings", "storage", "leagues"]);

// Mounted once in Layout. Slides My Account out from the nav bar as Setup-style cards.
export function MyAccountModal() {
    const { isOwner, hasAdminAccess } = useAuth();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("account");
    const cur = useRef({ open: false, tab: "home" });
    cur.current = { open, tab };
    useEffect(() => {
        const on = (e) => {
            const t = e.detail === "preferences" ? "account" : (e.detail || "account");
            if (cur.current.open && cur.current.tab === t) { setOpen(false); return; }
            setTab(t); setOpen(true);
        };
        const off = () => setOpen(false);
        window.addEventListener("open-account-modal", on);
        window.addEventListener("close-account-modal", off);
        return () => { window.removeEventListener("open-account-modal", on); window.removeEventListener("close-account-modal", off); };
    }, []);
    usePanelView(open ? `/account/${tab}` : null);
    useEffect(() => { window.dispatchEvent(new CustomEvent("account-panel-state", { detail: open ? tab : null })); }, [open, tab]);
    const tabs = ACCOUNT_TABS(isOwner, hasAdminAccess);
    const title = (tabs.find(([k]) => k === tab) || tabs[0])[1];
    return (
        <NavDropPanel open={open} onClose={() => setOpen(false)} kicker="My Account" title={title} active={tab}
            wide={WIDE.has(tab)} testid="my-account-modal">
            <MyAccount embedded tab={tab} onTab={setTab} onClose={() => setOpen(false)} switcher={<AccountSwitcher />} />
        </NavDropPanel>
    );
}

// The toggle button that sits next to the Search tile in the nav. Shows the
// account toggle when signed in, or a Sign In link when signed out.
export function AccountNavButton({ className = "", style, on, onToggle }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    if (user) {
        return (
            <button type="button" data-testid="nav-account-toggle" title="My Account" aria-pressed={on} onClick={onToggle || openAccountModal} style={style} className={className}>
                <UserCircle className="h-4 w-4" strokeWidth={2} />
                <span data-nav-label className="text-[8px] font-black uppercase tracking-[0.08em] leading-tight text-center">Account</span>
            </button>
        );
    }
    return (
        <button type="button" data-testid="nav-signin" title="Sign in" onClick={() => navigate("/login")} style={style} className={className}>
            <LogIn className="h-4 w-4" strokeWidth={2} />
            <span data-nav-label className="text-[8px] font-black uppercase tracking-[0.08em] leading-tight text-center">Sign In</span>
        </button>
    );
}
